import { useAllowedFeatureFilterOptions } from "@next-platform/client-common-core";
import { FilterActions } from "@next-platform/client-common-spicy-ui";
import type { SearchResults } from "algoliasearch-helper";
import React, { useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useClearRefinements, useInstantSearch, useRefinementList } from "react-instantsearch";

import getFacetRefinementsFromSearchState from "@/components/search/utils/getFacetRefinementsFromSearchState";

import { FilterSectionV2Id } from "../FiltersV2/FilterSectionV2Id";
import useFiltersV2Registry from "../FiltersV2/useFiltersV2Registry";
import useRegisterFilterSectionV2 from "../FiltersV2/useRegisterFilterSectionV2";
import applyExcludableRefinement from "./applyExcludableRefinement";
import { FEATURES_CATEGORY } from "./constants";
import FilterBoxFeatureSection from "./FilterBoxFeatureSection";
import getFeatureFilterList from "./getFeatureFilterList";
import getSupportedFeatureRefinements from "./getSupportedFeatureRefinements";
import skipWhileLoading from "./skipWhileLoading";

interface IFilterBoxFeaturesProps {
  results: SearchResults | undefined;
  isLoading: boolean;
  onAfterFeatureAction: () => void;
  portalTarget: HTMLElement | null;
}

const FilterBoxFeatures = ({ results, isLoading, onAfterFeatureAction, portalTarget }: IFilterBoxFeaturesProps) => {
  const isFiltersV2 = useFiltersV2Registry() !== null;
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const { items, refine } = useRefinementList({
    attribute: FEATURES_CATEGORY,
    operator: "and",
    limit: 100,
  });
  const { refine: clearFeatureRefinement } = useClearRefinements({
    includedAttributes: [FEATURES_CATEGORY],
  });
  const selectedRefinements = useMemo(
    () =>
      getFacetRefinementsFromSearchState({
        searchState: results?._state,
        attributeName: FEATURES_CATEGORY,
        facetOperator: "and",
      }),
    [results],
  );
  const allowedOptions = useAllowedFeatureFilterOptions();
  const selectedFeatureValues = indexUiState.refinementList?.[FEATURES_CATEGORY];

  useEffect(() => {
    if (!selectedFeatureValues?.length) {
      return;
    }
    const supportedFeatureValues = getSupportedFeatureRefinements(selectedFeatureValues, allowedOptions);
    if (supportedFeatureValues.length === selectedFeatureValues.length) {
      return;
    }
    setIndexUiState((previous) => ({
      ...previous,
      refinementList: {
        ...previous.refinementList,
        [FEATURES_CATEGORY]: supportedFeatureValues,
      },
    }));
  }, [allowedOptions, selectedFeatureValues, setIndexUiState]);

  const list = useMemo(
    () =>
      getFeatureFilterList({
        options: allowedOptions,
        items,
        selectedRefinements,
      }),
    [allowedOptions, items, selectedRefinements],
  );

  const applyFeatureAction = useCallback(
    (action: FilterActions, value?: string) => {
      if (action === FilterActions.INCLUDE || action === FilterActions.EXCLUDE) {
        applyExcludableRefinement({
          action,
          value: value ?? "",
          selectedRefinements,
          refine,
        });
        onAfterFeatureAction();
        return;
      }
      if (action === FilterActions.RESET) {
        if (!selectedRefinements.length) {
          return;
        }
        clearFeatureRefinement();
        onAfterFeatureAction();
      }
    },
    [clearFeatureRefinement, onAfterFeatureAction, refine, selectedRefinements],
  );

  const handleAction = useMemo(() => skipWhileLoading({ isLoading, actionHandler: applyFeatureAction }), [isLoading, applyFeatureAction]);

  useRegisterFilterSectionV2({ sectionId: FilterSectionV2Id.FEATURES, items: list, actionHandler: applyFeatureAction });

  if (!allowedOptions.length || !list.length || !portalTarget) {
    return null;
  }

  return createPortal(<FilterBoxFeatureSection actionHandler={handleAction} isFiltersV2={isFiltersV2} items={list} />, portalTarget);
};

export default React.memo(FilterBoxFeatures);
